import { ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Lock,
  Plus,
  Trash2,
} from "lucide-react";
import toast from "react-hot-toast";
import { useSearchVendors } from "@api/hooks/usePendingDocuments";
import { getVendorReferenceText } from "@/lib/vendorTaxIds";
import {
  useApprovalChains,
  type ApprovalExtractionFieldCondition,
  type ApprovalExtractionFieldOperator,
} from "@api/hooks/useApprovalChains";
import { useListUsers } from "@api/hooks/useUsers";
import { useListRoles } from "@api/hooks/useRoles";
import { useAllOrgUnits } from "@api/hooks/useOrgUnits";
import {
  SearchableSelect,
  type SearchableOption,
} from "@components/common/SearchableSelect";
import { MultiSearchableSelect } from "@components/common/MultiSearchableSelect";
import { InfoTooltip } from "@components/common/InfoTooltip";
import type { ApprovalChain } from "@api/hooks/useApprovalChains";
import { usePlanFeature } from "@api/hooks/useSubscription";
import { useOnboardingConfig } from "@api/hooks/useOnboarding";
import { FIELDS_BY_DOC_TYPE, type DocumentType } from "@/types/onboarding";

type WizardStep = "basic" | "conditions" | "steps" | "review";

interface CreateChainWizardProps {
  onClose: () => void;
  onSuccess: () => void;
  mode?: "create" | "edit";
  chainId?: string;
  initialData?: ApprovalChain;
  initialSimpleMode?: boolean;
}

// Action types supported by existing approval-chain data.
const ACTION_OPTIONS = [
  {
    value: "approve_extraction",
    label: "Approve Document Extraction",
    entity_type: "extraction",
  },
  {
    value: "discard_extraction",
    label: "Discard Document Extraction",
    entity_type: "extraction",
  },
  {
    value: "approve_ap_posting",
    label: "Approve AP Posting",
    entity_type: "ap_posting",
  },
  {
    value: "discard_ap_posting",
    label: "Discard AP Posting",
    entity_type: "ap_posting",
  },
] as const;

type ActionType = (typeof ACTION_OPTIONS)[number]["value"];

const ACTION_OPTION_BY_VALUE = new Map(
  ACTION_OPTIONS.map((option) => [option.value, option]),
);

const CREATE_ACTION_OPTIONS = ACTION_OPTIONS.filter(
  (option) =>
    option.value !== "discard_extraction" &&
    option.value !== "discard_ap_posting",
);

const DOCUMENT_TYPE_OPTIONS = [
  { id: "", label: "All Document Types" },
  { id: "invoice", label: "Invoice" },
  { id: "purchase_order", label: "Purchase Order" },
  { id: "receipt", label: "Receipt" },
];

const VENDOR_RISK_MIN_OPTIONS = [
  { id: "", label: "Any Risk Level" },
  { id: "low", label: "Low Risk and above" },
  { id: "medium", label: "Medium Risk and above" },
  { id: "high", label: "High Risk and above" },
  { id: "critical", label: "Critical Risk only" },
];

const VENDOR_RISK_MAX_OPTIONS = [
  { id: "", label: "No Limit" },
  { id: "low", label: "Low Risk only" },
  { id: "medium", label: "Low to Medium Risk" },
  { id: "high", label: "Low to High Risk" },
  { id: "critical", label: "All Risk Levels" },
];

const MANAGER_LEVEL_OPTIONS = [
  { id: "1", label: "1 - Direct Manager" },
  { id: "2", label: "2 - Manager's Manager" },
  { id: "3", label: "3 - Three Levels Up" },
  { id: "4", label: "4 - Four Levels Up" },
  { id: "5", label: "5 - Five Levels Up" },
];

type ExtractionFieldConditionDraft = ApprovalExtractionFieldCondition & {
  id: string;
  value: string;
};

const EXTRACTION_FIELD_OPERATORS: {
  value: ApprovalExtractionFieldOperator;
  label: string;
  requiresValue: boolean;
}[] = [
  { value: "eq", label: "equals", requiresValue: true },
  { value: "neq", label: "does not equal", requiresValue: true },
  { value: "contains", label: "contains", requiresValue: true },
  { value: "not_contains", label: "does not contain", requiresValue: true },
  { value: "gt", label: "greater than", requiresValue: true },
  { value: "gte", label: "greater than or equal to", requiresValue: true },
  { value: "lt", label: "less than", requiresValue: true },
  { value: "lte", label: "less than or equal to", requiresValue: true },
  { value: "exists", label: "is present", requiresValue: false },
  { value: "not_exists", label: "is missing", requiresValue: false },
];

const operatorRequiresValue = (operator: ApprovalExtractionFieldOperator) =>
  EXTRACTION_FIELD_OPERATORS.find((option) => option.value === operator)
    ?.requiresValue ?? true;

const humanizeFieldKey = (key: string) =>
  key
    .replace(/^line_items\./, "Line item ")
    .replace(/^lines\./, "Line item ")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const emptyStringToUndefined = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess(
    (value) => (value === "" ? undefined : value),
    schema.optional(),
  );

const isSelectableRef = (value: unknown): value is string =>
  typeof value === "string" && value.trim() !== "";

// Form schemas for each step
const BasicInfoSchema = z.object({
  name: z.string().min(1, "Chain name is required"),
  description: z.string().optional(),
  action_type: z.enum(
    ACTION_OPTIONS.map((option) => option.value) as [
      ActionType,
      ...ActionType[],
    ],
  ),
  document_type: emptyStringToUndefined(
    z.enum(["invoice", "purchase_order", "receipt"]),
  ),
});

// Conditions vary by action type
const ConditionsSchema = z.object({
  amount_min: z.string().optional(),
  amount_max: z.string().optional(),
  vendor_id: z.string().optional(),
  vendor_risk_min: emptyStringToUndefined(
    z.enum(["low", "medium", "high", "critical"]),
  ),
  vendor_risk_max: emptyStringToUndefined(
    z.enum(["low", "medium", "high", "critical"]),
  ),
  confidence_min: z.string().optional(),
  confidence_max: z.string().optional(),
  confidence_threshold: z.string().optional(),
  field_count_min: z.string().optional(),
  field_count_max: z.string().optional(),
});

// Approval step validation schema
const ApprovalStepSchema = z
  .object({
    step_number: z.number().int().positive(),
    approvers_type: z.enum(["user", "role", "manager", "org_unit"]),
    approver_ids: z.array(z.string()).optional(), // For 'user' type
    approver_roles: z.array(z.string()).optional(), // For 'role' type
    manager_level: z.number().int().positive().optional(), // For 'manager' type
    org_unit_id: z.string().optional(), // For 'manager' and 'org_unit' types
    parallel_approval: z.boolean().default(false),
    required_approvals: z.number().int().min(1).default(1),
    escalation_minutes: z.number().int().positive().optional(),
    auto_approve_after_minutes: z.number().int().positive().optional(),
  })
  .refine(
    (data) => {
      // User type requires at least one approver_id
      if (data.approvers_type === "user") {
        return data.approver_ids && data.approver_ids.length > 0;
      }
      return true;
    },
    { message: "At least one user must be selected", path: ["approver_ids"] },
  )
  .refine(
    (data) => {
      // Role type requires at least one approver_role
      if (data.approvers_type === "role") {
        return data.approver_roles && data.approver_roles.length > 0;
      }
      return true;
    },
    { message: "At least one role must be selected", path: ["approver_roles"] },
  )
  .refine(
    (data) => {
      // Manager type requires manager_level
      if (data.approvers_type === "manager") {
        return data.manager_level !== undefined && data.manager_level > 0;
      }
      return true;
    },
    { message: "Manager level must be specified", path: ["manager_level"] },
  )
  .refine(
    (data) => {
      // Manager and org_unit types require org_unit_id
      if (
        data.approvers_type === "manager" ||
        data.approvers_type === "org_unit"
      ) {
        return data.org_unit_id && data.org_unit_id.trim() !== "";
      }
      return true;
    },
    { message: "Organizational unit must be specified", path: ["org_unit_id"] },
  );

type BasicInfoFormData = z.input<typeof BasicInfoSchema>;
type ConditionsFormData = z.input<typeof ConditionsSchema>;
type ApprovalStepFormData = z.infer<typeof ApprovalStepSchema>;

const toPositiveNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return value;
  }

  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return undefined;
};

const normalizeStepForSchema = (
  step: any,
  index: number,
): ApprovalStepFormData => {
  const approversType =
    step.approvers_type ||
    step.type ||
    step.approver_type ||
    (step.assigned_user_id ? "user" : undefined) ||
    (step.manager_approval ? "manager" : undefined) ||
    (step.required_org_unit_type ? "org_unit" : undefined) ||
    (step.required_role ? "role" : undefined) ||
    "role";

  const approverIds = [
    ...(Array.isArray(step.approver_ids) ? step.approver_ids : []),
    step.selected_approver,
    step.approver_id,
    step.assigned_user_id,
  ].filter(
    (value): value is string =>
      typeof value === "string" && value.trim() !== "",
  );

  const approverRoles = [
    ...(Array.isArray(step.approver_roles) ? step.approver_roles : []),
    step.role,
    step.selected_role,
    step.required_role,
  ].filter(
    (value): value is string =>
      typeof value === "string" && value.trim() !== "",
  );

  return {
    step_number: index + 1,
    approvers_type: approversType,
    approver_ids:
      approversType === "user" && approverIds.length > 0
        ? Array.from(new Set(approverIds))
        : undefined,
    approver_roles:
      approversType === "role" && approverRoles.length > 0
        ? Array.from(new Set(approverRoles))
        : undefined,
    manager_level:
      approversType === "manager"
        ? toPositiveNumber(step.manager_level) ||
          (step.manager_approval ? 1 : undefined)
        : undefined,
    org_unit_id:
      (approversType === "manager" || approversType === "org_unit") &&
      typeof step.org_unit_id === "string" &&
      step.org_unit_id.trim() !== ""
        ? step.org_unit_id
        : undefined,
    parallel_approval: Boolean(step.parallel_approval),
    required_approvals: toPositiveNumber(step.required_approvals) || 1,
    escalation_minutes: toPositiveNumber(step.escalation_minutes),
    auto_approve_after_minutes: toPositiveNumber(
      step.auto_approve_after_minutes,
    ),
  };
};

const buildInitialConditions = (
  initialData?: ApprovalChain,
): ConditionsFormData => ({
  amount_min:
    initialData?.conditions?.amount_min != null
      ? String(initialData.conditions.amount_min)
      : "",
  amount_max:
    initialData?.conditions?.amount_max != null
      ? String(initialData.conditions.amount_max)
      : "",
  vendor_id:
    typeof initialData?.conditions?.vendor_id === "string"
      ? initialData.conditions.vendor_id
      : "",
  vendor_risk_min:
    initialData?.conditions?.vendor_risk_min === "low" ||
    initialData?.conditions?.vendor_risk_min === "medium" ||
    initialData?.conditions?.vendor_risk_min === "high" ||
    initialData?.conditions?.vendor_risk_min === "critical"
      ? initialData.conditions.vendor_risk_min
      : undefined,
  vendor_risk_max:
    initialData?.conditions?.vendor_risk_max === "low" ||
    initialData?.conditions?.vendor_risk_max === "medium" ||
    initialData?.conditions?.vendor_risk_max === "high" ||
    initialData?.conditions?.vendor_risk_max === "critical"
      ? initialData.conditions.vendor_risk_max
      : undefined,
  confidence_min:
    initialData?.conditions?.confidence_min != null
      ? String(initialData.conditions.confidence_min)
      : "",
  confidence_max:
    initialData?.conditions?.confidence_max != null
      ? String(initialData.conditions.confidence_max)
      : "",
  confidence_threshold:
    initialData?.conditions?.confidence_threshold != null
      ? String(initialData.conditions.confidence_threshold)
      : "",
  field_count_min:
    initialData?.conditions?.field_count_min != null
      ? String(initialData.conditions.field_count_min)
      : "",
  field_count_max:
    initialData?.conditions?.field_count_max != null
      ? String(initialData.conditions.field_count_max)
      : "",
});

const buildInitialExtractionFieldConditions = (
  initialData?: ApprovalChain,
): ExtractionFieldConditionDraft[] =>
  (initialData?.conditions?.extraction_field_conditions || [])
    .filter(
      (condition): condition is ApprovalExtractionFieldCondition =>
        typeof condition?.field === "string" &&
        typeof condition?.operator === "string",
    )
    .map((condition, index) => ({
      id: `field-condition-${index}-${condition.field}`,
      field: condition.field,
      operator: condition.operator,
      value: condition.value == null ? "" : String(condition.value),
    }));

const buildInitialSteps = (initialData?: ApprovalChain) =>
  (initialData?.steps || []).map((step) => ({
    id: step.id || `step-${step.step_number}`,
    step_number: step.step_number,
    type:
      step.approvers_type ||
      (step.assigned_user_id ? "user" : undefined) ||
      (step.manager_approval ? "manager" : undefined) ||
      (step.required_org_unit_type ? "org_unit" : undefined) ||
      (step.required_role ? "role" : undefined) ||
      "user",
    approvers_type:
      step.approvers_type ||
      (step.assigned_user_id ? "user" : undefined) ||
      (step.manager_approval ? "manager" : undefined) ||
      (step.required_org_unit_type ? "org_unit" : undefined) ||
      (step.required_role ? "role" : undefined) ||
      "user",
    selected_approver: step.approver_ids?.[0] || step.assigned_user_id || "",
    approver_ids:
      step.approver_ids ||
      (step.assigned_user_id ? [step.assigned_user_id] : []),
    role: step.approver_roles?.[0] || step.required_role || "",
    approver_roles:
      step.approver_roles || (step.required_role ? [step.required_role] : []),
    manager_level:
      step.manager_level || (step.manager_approval ? 1 : undefined),
    org_unit_id: step.org_unit_id,
    parallel_approval: step.parallel_approval || false,
    required_approvals: step.required_approvals || 1,
    escalation_minutes:
      step.escalate_after_hours != null
        ? step.escalate_after_hours * 60
        : undefined,
    auto_approve_after_minutes:
      step.auto_approve_after_hours != null
        ? step.auto_approve_after_hours * 60
        : undefined,
  }));

// Helper: checks whether a chain (or current steps+conditions) uses advanced-only features.
const chainHasAdvancedConfig = (data?: ApprovalChain): boolean => {
  if (!data) return false;
  const c = data.conditions ?? {};
  if (
    c.vendor_risk_min ||
    c.vendor_risk_max ||
    c.confidence_min != null ||
    c.confidence_max != null ||
    c.confidence_threshold != null ||
    c.field_count_min != null ||
    c.field_count_max != null ||
    (Array.isArray(c.extraction_field_conditions) &&
      c.extraction_field_conditions.length > 0)
  )
    return true;
  return (data.steps ?? []).some(
    (s) =>
      s.approvers_type === "manager" ||
      s.approvers_type === "org_unit" ||
      s.parallel_approval ||
      (s.required_approvals != null && s.required_approvals > 1) ||
      s.escalate_after_hours != null ||
      s.auto_approve_after_hours != null,
  );
};

export const CreateChainWizard = ({
  onClose,
  onSuccess,
  mode = "create",
  chainId,
  initialData,
  initialSimpleMode = true,
}: CreateChainWizardProps) => {
  const [currentStep, setCurrentStep] = useState<WizardStep>("basic");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stepsData, setStepsData] = useState<any[]>(() =>
    buildInitialSteps(initialData),
  );
  const [extractionFieldConditions, setExtractionFieldConditions] = useState<
    ExtractionFieldConditionDraft[]
  >(() => buildInitialExtractionFieldConditions(initialData));
  const [simpleMode, setSimpleMode] = useState(initialSimpleMode);

  const advancedEnabled = usePlanFeature("approval_chains_advanced");
  // If editing an existing chain that already has advanced config but plan doesn't allow it,
  // show a read-only warning and block saves that would mutate advanced state.
  const existingChainHasAdvanced =
    mode === "edit" && !advancedEnabled && chainHasAdvancedConfig(initialData);

  // Fetch approval chains
  const {
    createChain,
    updateChain,
    loading: chainLoading,
  } = useApprovalChains();

  // Form states for each step
  const basicForm = useForm<BasicInfoFormData>({
    resolver: zodResolver(BasicInfoSchema),
    defaultValues: {
      name: initialData?.name || "",
      description: initialData?.description || "",
      action_type:
        (initialData?.action_type as ActionType | undefined) ?? undefined,
      document_type:
        initialData?.document_type === "invoice" ||
        initialData?.document_type === "purchase_order" ||
        initialData?.document_type === "receipt"
          ? initialData.document_type
          : "invoice",
    },
  });

  const conditionsForm = useForm<ConditionsFormData>({
    resolver: zodResolver(ConditionsSchema),
    defaultValues: buildInitialConditions(initialData),
  });

  // Step progression mapping
  const stepSequence: WizardStep[] = ["basic", "conditions", "steps", "review"];

  const getCurrentStepIndex = () => stepSequence.indexOf(currentStep);

  const getNextStep = (): WizardStep | null => {
    const currentIndex = getCurrentStepIndex();
    if (currentIndex < stepSequence.length - 1) {
      return stepSequence[currentIndex + 1];
    }
    return null;
  };

  const getPreviousStep = (): WizardStep | null => {
    const currentIndex = getCurrentStepIndex();
    if (currentIndex > 0) {
      return stepSequence[currentIndex - 1];
    }
    return null;
  };

  const buildExtractionFieldConditionPayload = ():
    ApprovalExtractionFieldCondition[] | null => {
    const conditions: ApprovalExtractionFieldCondition[] = [];

    for (const condition of extractionFieldConditions) {
      const field = condition.field?.trim();
      const operator = condition.operator || "eq";
      const value =
        typeof condition.value === "string"
          ? condition.value.trim()
          : condition.value;

      if (!field && !value) {
        continue;
      }
      if (!field) {
        toast.error("Select a field for each extraction field condition");
        return null;
      }
      if (
        operatorRequiresValue(operator) &&
        (value === undefined || value === "")
      ) {
        toast.error(`Enter a value for ${humanizeFieldKey(field)}`);
        return null;
      }

      conditions.push(
        operatorRequiresValue(operator)
          ? { field, operator, value }
          : { field, operator },
      );
    }

    return conditions;
  };

  const handleNextStep = async () => {
    // Validate current step before moving forward
    let isValid = true;

    switch (currentStep) {
      case "basic":
        isValid = await basicForm.trigger();
        break;
      case "conditions":
        isValid = await conditionsForm.trigger();
        if (isValid && buildExtractionFieldConditionPayload() === null) {
          isValid = false;
        }
        break;
      case "steps":
        // Validate that at least one step is added
        if (stepsData.length === 0) {
          toast.error("Please add at least one approval step");
          isValid = false;
          break;
        }

        // Validate each step against the schema
        for (const step of stepsData) {
          try {
            ApprovalStepSchema.parse(
              normalizeStepForSchema(step, stepsData.indexOf(step)),
            );
          } catch (error: any) {
            const errorMsg =
              error.errors?.[0]?.message || "Invalid step configuration";
            toast.error(`Step ${stepsData.indexOf(step) + 1}: ${errorMsg}`);
            isValid = false;
            break;
          }
        }
        break;
      case "review":
        // Review step has no validation
        break;
    }

    if (!isValid) return;

    // Move to next step
    const nextStep = getNextStep();
    if (nextStep) {
      setCurrentStep(nextStep);
    }
  };

  const handlePreviousStep = () => {
    const previousStep = getPreviousStep();
    if (previousStep) {
      setCurrentStep(previousStep);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const basicData = basicForm.getValues();
      const conditionsData = conditionsForm.getValues();
      const documentType =
        typeof basicData.document_type === "string"
          ? basicData.document_type
          : undefined;
      const extractionFieldConditionPayload =
        buildExtractionFieldConditionPayload();
      if (extractionFieldConditionPayload === null) {
        setIsSubmitting(false);
        return;
      }

      // Validate we have at least one step
      if (stepsData.length === 0) {
        toast.error("Please add at least one approval step");
        setIsSubmitting(false);
        return;
      }

      // Build conditions object, filtering out undefined values
      const vendorId = isSelectableRef(conditionsData.vendor_id)
        ? conditionsData.vendor_id
        : undefined;
      const conditions = Object.fromEntries(
        Object.entries({
          ...conditionsData,
          vendor_id: vendorId,
          extraction_field_conditions:
            extractionFieldConditionPayload.length > 0
              ? extractionFieldConditionPayload
              : undefined,
        }).filter(([, v]) => v !== undefined && v !== ""),
      );

      // Transform steps data to match backend format
      const steps = stepsData.map((step, index) =>
        normalizeStepForSchema(step, index),
      );

      const chainData = {
        name: basicData.name,
        description: basicData.description || "",
        action_type: basicData.action_type,
        entity_type:
          ACTION_OPTIONS.find(
            (option) => option.value === basicData.action_type,
          )?.entity_type ?? "extraction",
        document_type: documentType,
        conditions: Object.keys(conditions).length > 0 ? conditions : {},
        steps: steps,
        is_enabled: true,
        is_active: true,
      };

      if (mode === "edit") {
        if (!chainId) {
          throw new Error("Chain ID is required to update an approval chain");
        }
        await updateChain(chainId, chainData);
        toast.success("Approval chain updated successfully");
      } else {
        await createChain(chainData);
        toast.success("Approval chain created successfully");
      }
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Chain submission error:", error);
      toast.error(
        error.message ||
          (mode === "edit"
            ? "Failed to update approval chain"
            : "Failed to create approval chain"),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800">
      {/* Progress Indicator */}
      <div className="border-b border-gray-200 dark:border-gray-800 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-50">
            {mode === "edit" ? "Edit Approval Chain" : "Create Approval Chain"}
          </h2>
          <div className="flex items-center gap-4">
            {/* Simple/Advanced Mode Toggle */}
            <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setSimpleMode(true)}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  simpleMode
                    ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-50 shadow"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-50"
                }`}
              >
                Simple
              </button>
              <button
                onClick={() => setSimpleMode(false)}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  !simpleMode
                    ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-50 shadow"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-50"
                }`}
              >
                Advanced
              </button>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          {(["basic", "conditions", "steps", "review"] as const).map(
            (step, index) => (
              <div key={step} className="flex items-center flex-1">
                <div
                  className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                    currentStep === step
                      ? "bg-blue-600 text-white"
                      : ["basic", "conditions", "steps", "review"].indexOf(
                            currentStep,
                          ) > index
                        ? "bg-green-600 text-white"
                        : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                  }`}
                >
                  {index + 1}
                </div>
                {index < 3 && (
                  <div
                    className={`flex-1 h-1 mx-2 transition-colors ${
                      ["basic", "conditions", "steps", "review"].indexOf(
                        currentStep,
                      ) > index
                        ? "bg-green-600"
                        : "bg-gray-200 dark:bg-gray-700"
                    }`}
                  ></div>
                )}
              </div>
            ),
          )}
        </div>

        <div className="flex justify-between mt-4 text-xs font-medium text-gray-600 dark:text-gray-400">
          <span>Basic Info</span>
          <span>Conditions</span>
          <span>Steps</span>
          <span>Review</span>
        </div>
      </div>

      {/* Pro-feature warning banner for existing advanced chains on non-pro plan */}
      {existingChainHasAdvanced && (
        <div className="mx-6 mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-2 text-sm text-amber-800 dark:text-amber-200">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>
            This chain uses Pro features (manager hierarchy, org units,
            escalation, or advanced conditions). Saving on your current plan
            would strip those settings.{" "}
            <Link
              to="/admin/settings?tab=billing"
              className="underline font-medium"
            >
              Upgrade to keep them.
            </Link>
          </span>
        </div>
      )}

      {/* Step Content */}
      <div className="p-6">
        {currentStep === "basic" && (
          <BasicInfoStep form={basicForm} simpleMode={simpleMode} mode={mode} />
        )}

        {currentStep === "conditions" && (
          <ConditionsStep
            form={conditionsForm}
            simpleMode={simpleMode}
            actionType={basicForm.watch("action_type")}
            documentType={basicForm.watch("document_type")}
            advancedEnabled={advancedEnabled}
            extractionFieldConditions={extractionFieldConditions}
            onExtractionFieldConditionsChange={setExtractionFieldConditions}
          />
        )}

        {currentStep === "steps" && (
          <StepsStep
            documentType={basicForm.watch("document_type")}
            simpleMode={simpleMode}
            steps={stepsData}
            onStepsChange={setStepsData}
            advancedEnabled={advancedEnabled}
          />
        )}

        {currentStep === "review" && (
          <ReviewStep
            basicData={basicForm.getValues()}
            conditionsData={conditionsForm.getValues()}
            extractionFieldConditions={extractionFieldConditions}
          />
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="border-t border-gray-200 dark:border-gray-800 p-6 flex justify-between">
        <button
          onClick={handlePreviousStep}
          disabled={!getPreviousStep()}
          className="inline-flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Back
        </button>

        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>

          {currentStep !== "review" ? (
            <button
              onClick={handleNextStep}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={
                isSubmitting || chainLoading || existingChainHasAdvanced
              }
              title={
                existingChainHasAdvanced
                  ? "Upgrade your plan to save changes to this chain"
                  : undefined
              }
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting || chainLoading
                ? mode === "edit"
                  ? "Saving..."
                  : "Creating..."
                : mode === "edit"
                  ? "Save Changes"
                  : "Create Chain"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ProFeatureBadge: inline upgrade nudge shown in place of locked controls
const ProFeatureBadge = ({ children }: { children: ReactNode }) => (
  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 py-2">
    <Lock className="h-3 w-3 flex-shrink-0" />
    <span>{children}</span>
    <Link
      to="/admin/settings?tab=billing"
      className="underline text-blue-600 dark:text-blue-400 whitespace-nowrap"
    >
      Upgrade
    </Link>
  </div>
);

// Step Components
const BasicInfoStep = ({ form, simpleMode, mode }: any) => {
  const selectedAction = form.watch("action_type");
  const selectedLegacyOption = ACTION_OPTION_BY_VALUE.get(selectedAction);
  const actionOptions =
    mode === "edit" && selectedLegacyOption
      ? Array.from(
          new Map(
            [...CREATE_ACTION_OPTIONS, selectedLegacyOption].map((option) => [
              option.value,
              option,
            ]),
          ).values(),
        )
      : CREATE_ACTION_OPTIONS;

  // Map action types to their descriptions
  const actionDescriptions: Record<ActionType, string> = {
    approve_extraction:
      "Approval flow for document extraction review decisions.",
    discard_extraction:
      "Discard flow for document extraction review decisions.",
    approve_ap_posting: "Approval flow for AP postings created from invoices.",
    discard_ap_posting: "Discard flow for AP postings that should not proceed.",
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
          Chain Name *
        </label>
        <input
          {...form.register("name")}
          placeholder="e.g., High-Value Extraction Review"
          className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
        />
        {form.formState.errors.name && (
          <p className="text-red-600 text-sm mt-1">
            {form.formState.errors.name.message}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
          Description
        </label>
        <textarea
          {...form.register("description")}
          placeholder="Describe when this chain should be used"
          rows={3}
          className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
          Action Type *
        </label>
        <SearchableSelect
          value={form.watch("action_type") || ""}
          onChange={(value) =>
            form.setValue("action_type", value as ActionType, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
          options={[
            { id: "", label: "Select an action..." },
            ...actionOptions.map((action) => ({
              id: action.value,
              label: action.label,
            })),
          ]}
          placeholder="Select an action..."
          clearable={false}
        />
        {form.formState.errors.action_type && (
          <p className="text-red-600 text-sm mt-1">
            {form.formState.errors.action_type.message}
          </p>
        )}
        {selectedAction && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            {actionDescriptions[selectedAction as ActionType]}
          </p>
        )}
      </div>

      {selectedAction && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
          Entity Type:{" "}
          {ACTION_OPTIONS.find(
            (option) => option.value === selectedAction,
          )?.entity_type.replace(/_/g, " ")}
        </div>
      )}

      <div>
          <div className="mb-2 flex items-center gap-1.5">
            <label className="text-sm font-medium text-gray-900 dark:text-gray-50">Document Type</label>
            <InfoTooltip>Leave this unset to apply the chain to every document type.</InfoTooltip>
          </div>
          <SearchableSelect
            value={form.watch("document_type") || ""}
            onChange={(value) =>
              form.setValue("document_type", value as DocumentType | "", {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            options={DOCUMENT_TYPE_OPTIONS}
            placeholder="All Document Types"
            clearable={false}
          />
      </div>

    </div>
  );
};

const ConditionsStep = ({
  form,
  simpleMode,
  actionType,
  documentType,
  advancedEnabled,
  extractionFieldConditions,
  onExtractionFieldConditionsChange,
}: any) => {
  const [vendorSearchTerm, setVendorSearchTerm] = useState("");
  const { data: vendorsData, isLoading: loadingVendors } =
    useSearchVendors(vendorSearchTerm);
  const { data: onboardingConfig } = useOnboardingConfig(advancedEnabled);
  const vendors = vendorsData?.items || [];

  const selectedAction = ACTION_OPTIONS.find(
    (option) => option.value === actionType,
  );
  const isExtractionAction = selectedAction?.entity_type === "extraction";
  const isAPPostingAction = selectedAction?.entity_type === "ap_posting";
  const isCorrectionsAction = false;
  const effectiveDocumentType: DocumentType =
    (isAPPostingAction ? "invoice" : documentType) || "invoice";
  const configuredFieldKeys =
    onboardingConfig?.extraction_fields?.[effectiveDocumentType] || [];
  const fieldMetadata = FIELDS_BY_DOC_TYPE[effectiveDocumentType] || [];
  const extractionFieldOptions = configuredFieldKeys.map((fieldKey: string) => {
    const metadata = fieldMetadata.find((field) => field.key === fieldKey);
    return {
      key: fieldKey,
      label: metadata?.label || humanizeFieldKey(fieldKey),
    };
  });

  const updateFieldCondition = (
    id: string,
    updates: Partial<ExtractionFieldConditionDraft>,
  ) => {
    onExtractionFieldConditionsChange(
      extractionFieldConditions.map(
        (condition: ExtractionFieldConditionDraft) =>
          condition.id === id ? { ...condition, ...updates } : condition,
      ),
    );
  };

  const addFieldCondition = () => {
    const firstField = extractionFieldOptions[0]?.key || "";
    onExtractionFieldConditionsChange([
      ...extractionFieldConditions,
      {
        id: `field-condition-${Date.now()}`,
        field: firstField,
        operator: "eq",
        value: "",
      },
    ]);
  };

  const removeFieldCondition = (id: string) => {
    onExtractionFieldConditionsChange(
      extractionFieldConditions.filter(
        (condition: ExtractionFieldConditionDraft) => condition.id !== id,
      ),
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">When should this chain apply?</h3>
        <InfoTooltip>Leave a condition unset to match any value.</InfoTooltip>
      </div>

      {/* Amount-based conditions (common to all) */}
      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
            Minimum Amount
          </label>
          <input
            {...form.register("amount_min")}
            type="number"
            placeholder="e.g., 5000"
            step="0.01"
            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
            Maximum Amount
          </label>
          <input
            {...form.register("amount_max")}
            type="number"
            placeholder="e.g., 50000"
            step="0.01"
            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
          />
        </div>
      </div>

      {/* Vendor Selection (optional for all action types) */}
      <VendorSelector
        value={form.watch("vendor_id") || ""}
        onChange={(id) => form.setValue("vendor_id", id)}
        vendors={vendors}
        isLoading={loadingVendors}
        onSearchChange={setVendorSearchTerm}
        searchTerm={vendorSearchTerm}
      />

      {/* Vendor Risk (Pro feature) */}
      {advancedEnabled ? (
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
              Minimum Vendor Risk
            </label>
            <SearchableSelect
              value={form.watch("vendor_risk_min") || ""}
              onChange={(value) =>
                form.setValue("vendor_risk_min", value, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
              options={VENDOR_RISK_MIN_OPTIONS}
              placeholder="Any Risk Level"
              clearable={false}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Apply chain only if vendor risk is at or above this level
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
              Maximum Vendor Risk
            </label>
            <SearchableSelect
              value={form.watch("vendor_risk_max") || ""}
              onChange={(value) =>
                form.setValue("vendor_risk_max", value, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
              options={VENDOR_RISK_MAX_OPTIONS}
              placeholder="No Limit"
              clearable={false}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Apply chain only if vendor risk is at or below this level
            </p>
          </div>
        </div>
      ) : (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-800/50">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Vendor Risk Filtering
          </p>
          <ProFeatureBadge>
            Vendor risk conditions require Pro plan
          </ProFeatureBadge>
        </div>
      )}

      {/* Extraction Confidence (Extraction actions) — Pro feature */}
      {isExtractionAction &&
        (advancedEnabled ? (
          <div className="space-y-4">
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-3">
                Confidence Threshold for Approval
              </h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
                    Approval Threshold (0-100)
                  </label>
                  <input
                    {...form.register("confidence_threshold")}
                    type="number"
                    placeholder="e.g., 85"
                    step="0.01"
                    min="0"
                    max="100"
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
                  />
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                    Match extractions at or below this confidence. The system
                    fallback threshold is 85.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
                  Minimum Confidence Range
                </label>
                <input
                  {...form.register("confidence_min")}
                  type="number"
                  placeholder="e.g., 75"
                  step="0.01"
                  min="0"
                  max="100"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Optional: Match when above this value
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
                  Maximum Confidence Range
                </label>
                <input
                  {...form.register("confidence_max")}
                  type="number"
                  placeholder="e.g., 95"
                  step="0.01"
                  min="0"
                  max="100"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Optional: Match when below this value
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-800/50">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Confidence-Based Routing
            </p>
            <ProFeatureBadge>
              Confidence threshold conditions require Pro plan
            </ProFeatureBadge>
          </div>
        ))}

      {/* Field Count (Extraction and Corrections) — Pro feature */}
      {(isExtractionAction || isCorrectionsAction) &&
        (advancedEnabled ? (
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
                Minimum Field Count
              </label>
              <input
                {...form.register("field_count_min")}
                type="number"
                placeholder="e.g., 5"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
                Maximum Field Count
              </label>
              <input
                {...form.register("field_count_max")}
                type="number"
                placeholder="e.g., 20"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
              />
            </div>
          </div>
        ) : (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-800/50">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Field Count Filtering
            </p>
            <ProFeatureBadge>
              Field count conditions require Pro plan
            </ProFeatureBadge>
          </div>
        ))}

      {/* Tenant-selected extraction fields (Pro feature) */}
      {advancedEnabled ? (
        <div className="space-y-3 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
                Extraction Field Conditions
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {effectiveDocumentType.replace(/_/g, " ")} fields selected
                during onboarding
              </p>
            </div>
            <button
              type="button"
              onClick={addFieldCondition}
              disabled={extractionFieldOptions.length === 0}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Add extraction field condition"
            >
              <Plus className="h-4 w-4" />
              Add
            </button>
          </div>

          {extractionFieldOptions.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Complete onboarding field selection before adding extraction field
              conditions.
            </p>
          ) : extractionFieldConditions.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No extraction field conditions.
            </p>
          ) : (
            <div className="space-y-3">
              {extractionFieldConditions.map(
                (condition: ExtractionFieldConditionDraft) => {
                  const requiresValue = operatorRequiresValue(
                    condition.operator,
                  );
                  return (
                    <div
                      key={condition.id}
                      className="grid grid-cols-1 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_auto] gap-3 items-center"
                    >
                      <SearchableSelect
                        value={condition.field}
                        onChange={(value) =>
                          updateFieldCondition(condition.id, { field: value })
                        }
                        options={extractionFieldOptions.map((option) => ({
                          id: option.key,
                          label: option.label,
                        }))}
                        placeholder="Field"
                        clearable={false}
                      />

                      <SearchableSelect
                        value={condition.operator}
                        onChange={(value) =>
                          updateFieldCondition(condition.id, {
                            operator: value as ApprovalExtractionFieldOperator,
                          })
                        }
                        options={EXTRACTION_FIELD_OPERATORS.map((option) => ({
                          id: option.value,
                          label: option.label,
                        }))}
                        placeholder="Operator"
                        clearable={false}
                      />

                      <input
                        type="text"
                        value={condition.value}
                        onChange={(event) =>
                          updateFieldCondition(condition.id, {
                            value: event.target.value,
                          })
                        }
                        disabled={!requiresValue}
                        placeholder={requiresValue ? "Value" : ""}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50 disabled:bg-gray-100 dark:disabled:bg-gray-800/50"
                      />

                      <button
                        type="button"
                        onClick={() => removeFieldCondition(condition.id)}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                        title="Remove extraction field condition"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  );
                },
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-800/50">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Extraction Field Conditions
          </p>
          <ProFeatureBadge>
            Extraction field conditions require Pro plan
          </ProFeatureBadge>
        </div>
      )}

    </div>
  );
};

const StepsStep = ({
  documentType,
  simpleMode,
  steps,
  onStepsChange,
  advancedEnabled,
}: any) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Define Approval Steps</h3>
        <InfoTooltip>Steps are completed in the order shown.</InfoTooltip>
      </div>

      {simpleMode ? (
        <SimpleStepsBuilder
          steps={steps}
          onStepsChange={onStepsChange}
          advancedEnabled={advancedEnabled}
        />
      ) : (
        <AdvancedStepsBuilder
          steps={steps}
          onStepsChange={onStepsChange}
          advancedEnabled={advancedEnabled}
        />
      )}
    </div>
  );
};

const SimpleStepsBuilder = ({ steps, onStepsChange, advancedEnabled }: any) => {
  const handleAddStep = () => {
    onStepsChange([
      ...steps,
      {
        id: `step-${Date.now()}`,
        step_number: steps.length + 1,
        type: "role",
        required_role: "",
      },
    ]);
  };

  const handleRemoveStep = (stepId: string) => {
    onStepsChange(steps.filter((s: any) => s.id !== stepId));
  };

  const handleUpdateStep = (stepId: string, updated: any) => {
    const newSteps = steps.map((s: any) => (s.id === stepId ? updated : s));
    onStepsChange(newSteps);
  };

  return (
    <div className="space-y-4">
      <button
        onClick={handleAddStep}
        className="w-full px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        + Add Step
      </button>

      <div className="space-y-3">
        {steps.map((step: any, index: number) => (
          <div
            key={step.id}
            className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-start justify-between mb-3">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Step {index + 1}
              </span>
              <button
                onClick={() => handleRemoveStep(step.id)}
                className="text-red-600 hover:text-red-700 text-sm"
              >
                Remove
              </button>
            </div>

            <SimpleApproverSelector
              step={step}
              onChange={(updated: any) => handleUpdateStep(step.id, updated)}
              advancedEnabled={advancedEnabled}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const AdvancedStepsBuilder = ({
  steps,
  onStepsChange,
  advancedEnabled,
}: any) => {
  const handleAddStep = () => {
    onStepsChange([
      ...steps,
      {
        id: `step-${Date.now()}`,
        step_number: steps.length + 1,
        required_role: "",
        escalate_after_hours: 24,
        auto_approve_after_hours: 48,
      },
    ]);
  };

  const handleRemoveStep = (stepId: string) => {
    onStepsChange(steps.filter((s: any) => s.id !== stepId));
  };

  const handleUpdateStep = (stepId: string, updated: any) => {
    const newSteps = steps.map((s: any) => (s.id === stepId ? updated : s));
    onStepsChange(newSteps);
  };

  return (
    <div className="space-y-4">
      <button
        onClick={handleAddStep}
        className="w-full px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        + Add Step
      </button>

      <div className="space-y-3">
        {steps.map((step: any, index: number) => (
          <div
            key={step.id}
            className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-start justify-between mb-3">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Step {index + 1}
              </span>
              <button
                onClick={() => handleRemoveStep(step.id)}
                className="text-red-600 hover:text-red-700 text-sm"
              >
                Remove
              </button>
            </div>

            <AdvancedApproverSelector
              step={step}
              onChange={(updated: any) => handleUpdateStep(step.id, updated)}
              advancedEnabled={advancedEnabled}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const APPROVER_TYPES_BASIC = ["user", "role"] as const;
const APPROVER_TYPES_ADVANCED = ["manager", "org_unit"] as const;

const SimpleApproverSelector = ({ step, onChange, advancedEnabled }: any) => {
  const approversType = step.approvers_type || step.type || "role";
  const { data: usersData, isLoading: loadingUsers } = useListUsers(0, 1000);
  const { data: rolesData, isLoading: loadingRoles } = useListRoles(0, 1000);
  const { data: orgUnitsData, isLoading: loadingOrgUnits } = useAllOrgUnits();

  const handleApproverTypeChange = (newType: string) => {
    // Reset type-specific fields when changing type
    const updated = { ...step, approvers_type: newType, type: newType };
    delete updated.approver_ids;
    delete updated.approver_roles;
    delete updated.manager_level;
    delete updated.org_unit_id;
    onChange(updated);
  };

  const getApproverTypeDescription = (type: string) => {
    const descriptions: Record<string, string> = {
      role: "Any user with the selected role(s) can approve",
      manager:
        "Manager in the selected org unit approves (1 = direct manager, 2 = manager's manager, etc.)",
      user: "Specific user(s) must approve",
      org_unit: "Any member of the selected org unit can approve",
    };
    return descriptions[type] || "";
  };

  // Transform API data to SearchableOption format
  const userOptions: SearchableOption[] = (usersData?.items || []).map(
    (user: any) => ({
      id: user.id,
      label: `${user.first_name} ${user.last_name}`,
      description: user.email,
    }),
  );

  const roleOptions: SearchableOption[] = (rolesData?.items || []).map(
    (role: any) => ({
      id: role.name,
      label: role.name,
      description: role.description,
    }),
  );

  const orgUnitOptions: SearchableOption[] = (orgUnitsData || []).map(
    (unit: any) => ({
      id: unit.id,
      label: unit.name,
      description: unit.unit_type,
    }),
  );

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
          Approver Type *
        </label>
        <SearchableSelect
          value={
            advancedEnabled
              ? approversType
              : APPROVER_TYPES_ADVANCED.includes(approversType)
                ? "role"
                : approversType
          }
          onChange={handleApproverTypeChange}
          options={[
            { id: "role", label: "Role-Based Approval" },
            { id: "user", label: "Specific User(s)" },
            ...(advancedEnabled
              ? [
                  { id: "manager", label: "Manager Hierarchy" },
                  { id: "org_unit", label: "Organizational Unit" },
                ]
              : []),
          ]}
          placeholder="Approver type"
          clearable={false}
        />
        {!advancedEnabled && (
          <ProFeatureBadge>
            Manager and org unit approver types require Pro plan
          </ProFeatureBadge>
        )}
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {getApproverTypeDescription(approversType)}
        </p>
      </div>

      {/* Role Type */}
      {approversType === "role" && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
              Role *
            </label>
            <SearchableSelect
              options={roleOptions}
              value={step.approver_roles?.[0] || ""}
              onChange={(id) =>
                onChange({ ...step, role: id, approver_roles: [id] })
              }
              placeholder="Search and select a role..."
              isLoading={loadingRoles}
              emptyMessage="No roles found"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Select the role that can approve (multiple roles in advanced mode)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
              Number of Approvals Needed *
            </label>
            <input
              type="number"
              min="1"
              value={step.required_approvals || 1}
              onChange={(e) =>
                onChange({
                  ...step,
                  required_approvals: parseInt(e.target.value),
                })
              }
              className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              How many users with this role must approve
            </p>
          </div>
        </>
      )}

      {/* User Type */}
      {approversType === "user" && (
        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
            User *
          </label>
          <SearchableSelect
            options={userOptions}
            value={step.approver_ids?.[0] || ""}
            onChange={(id) =>
              onChange({ ...step, selected_approver: id, approver_ids: [id] })
            }
            placeholder="Search and select a user..."
            isLoading={loadingUsers}
            emptyMessage="No users found"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Select the user who must approve (multiple users in advanced mode)
          </p>
        </div>
      )}

      {/* Manager Type */}
      {approversType === "manager" && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
              Manager Level *
            </label>
            <SearchableSelect
              value={String(step.manager_level || 1)}
              onChange={(value) =>
                onChange({ ...step, manager_level: parseInt(value) })
              }
              options={MANAGER_LEVEL_OPTIONS}
              placeholder="Manager level"
              clearable={false}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              How many levels up the manager hierarchy to approve
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
              Organizational Unit *
            </label>
            <SearchableSelect
              options={orgUnitOptions}
              value={step.org_unit_id || ""}
              onChange={(id) => onChange({ ...step, org_unit_id: id })}
              placeholder="Search and select an org unit..."
              isLoading={loadingOrgUnits}
              emptyMessage="No org units found"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              The org unit defines the manager context (users can have different
              managers in different units)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
              Number of Approvals Needed *
            </label>
            <input
              type="number"
              min="1"
              value={step.required_approvals || 1}
              onChange={(e) =>
                onChange({
                  ...step,
                  required_approvals: parseInt(e.target.value),
                })
              }
              className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
            />
          </div>
        </>
      )}

      {/* Org Unit Type */}
      {approversType === "org_unit" && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
              Organizational Unit *
            </label>
            <SearchableSelect
              options={orgUnitOptions}
              value={step.org_unit_id || ""}
              onChange={(id) => onChange({ ...step, org_unit_id: id })}
              placeholder="Search and select an org unit..."
              isLoading={loadingOrgUnits}
              emptyMessage="No org units found"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Any member of this org unit can approve
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
              Number of Approvals Needed *
            </label>
            <input
              type="number"
              min="1"
              value={step.required_approvals || 1}
              onChange={(e) =>
                onChange({
                  ...step,
                  required_approvals: parseInt(e.target.value),
                })
              }
              className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              How many members must approve
            </p>
          </div>
        </>
      )}

      {/* Escalation and Auto-Approval — Pro feature */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-50 mb-3">
          Escalation & Auto-Approval (Optional)
        </h4>

        {advancedEnabled ? (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
                Escalate After (minutes)
              </label>
              <input
                type="number"
                value={step.escalation_minutes || ""}
                onChange={(e) =>
                  onChange({
                    ...step,
                    escalation_minutes: e.target.value
                      ? parseInt(e.target.value)
                      : undefined,
                  })
                }
                placeholder="e.g., 1440"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Leave empty to disable escalation
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
                Auto-Approve After (minutes)
              </label>
              <input
                type="number"
                value={step.auto_approve_after_minutes || ""}
                onChange={(e) =>
                  onChange({
                    ...step,
                    auto_approve_after_minutes: e.target.value
                      ? parseInt(e.target.value)
                      : undefined,
                  })
                }
                placeholder="e.g., 2880"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Leave empty to disable auto-approval
              </p>
            </div>
          </div>
        ) : (
          <ProFeatureBadge>
            Escalation and auto-approval settings require Pro plan
          </ProFeatureBadge>
        )}
      </div>
    </div>
  );
};

const AdvancedApproverSelector = ({ step, onChange, advancedEnabled }: any) => {
  const approversType = step.approvers_type || step.type || "role";
  const { data: usersData, isLoading: loadingUsers } = useListUsers(0, 1000);
  const { data: rolesData, isLoading: loadingRoles } = useListRoles(0, 1000);
  const { data: orgUnitsData, isLoading: loadingOrgUnits } = useAllOrgUnits();

  const handleApproverTypeChange = (newType: string) => {
    const updated = { ...step, approvers_type: newType, type: newType };
    delete updated.approver_ids;
    delete updated.approver_roles;
    delete updated.manager_level;
    delete updated.org_unit_id;
    onChange(updated);
  };

  // Transform API data to SearchableOption format
  const userOptions: SearchableOption[] = (usersData?.items || []).map(
    (user: any) => ({
      id: user.id,
      label: `${user.first_name} ${user.last_name}`,
      description: user.email,
    }),
  );

  const roleOptions: SearchableOption[] = (rolesData?.items || []).map(
    (role: any) => ({
      id: role.name,
      label: role.name,
      description: role.description,
    }),
  );

  const orgUnitOptions: SearchableOption[] = (orgUnitsData || []).map(
    (unit: any) => ({
      id: unit.id,
      label: unit.name,
      description: unit.unit_type,
    }),
  );

  return (
    <div className="space-y-4">
      {/* Approver Type Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
          Approver Type *
        </label>
        <SearchableSelect
          value={
            advancedEnabled
              ? approversType
              : APPROVER_TYPES_ADVANCED.includes(approversType)
                ? "role"
                : approversType
          }
          onChange={handleApproverTypeChange}
          options={[
            { id: "role", label: "Role-Based Approval" },
            { id: "user", label: "Specific User(s)" },
            ...(advancedEnabled
              ? [
                  { id: "manager", label: "Manager Hierarchy" },
                  { id: "org_unit", label: "Organizational Unit" },
                ]
              : []),
          ]}
          placeholder="Approver type"
          clearable={false}
        />
        {!advancedEnabled && (
          <ProFeatureBadge>
            Manager and org unit approver types require Pro plan
          </ProFeatureBadge>
        )}
      </div>

      {/* Role Type */}
      {approversType === "role" && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
              Role(s) *
            </label>
            <MultiSearchableSelect
              options={roleOptions}
              value={step.approver_roles || []}
              onChange={(ids) => onChange({ ...step, approver_roles: ids })}
              placeholder="Search and select roles..."
              isLoading={loadingRoles}
              emptyMessage="No roles found"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Select one or more roles that can approve
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
                Number of Approvals Needed *
              </label>
              <input
                type="number"
                min="1"
                value={step.required_approvals || 1}
                onChange={(e) =>
                  onChange({
                    ...step,
                    required_approvals: parseInt(e.target.value),
                  })
                }
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
              />
            </div>

            <div>
              <label className="flex items-center mt-8">
                <input
                  type="checkbox"
                  checked={step.parallel_approval || false}
                  onChange={(e) =>
                    onChange({ ...step, parallel_approval: e.target.checked })
                  }
                  className="w-4 h-4 rounded"
                />
                <span className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-50">
                  Parallel Approval
                </span>
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Approvals happen simultaneously
              </p>
            </div>
          </div>
        </>
      )}

      {/* User Type */}
      {approversType === "user" && (
        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
            User(s) *
          </label>
          <MultiSearchableSelect
            options={userOptions}
            value={step.approver_ids || []}
            onChange={(ids) => onChange({ ...step, approver_ids: ids })}
            placeholder="Search and select users..."
            isLoading={loadingUsers}
            emptyMessage="No users found"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Select one or more users who must approve
          </p>
        </div>
      )}

      {/* Manager Type */}
      {approversType === "manager" && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
              Manager Level *
            </label>
            <SearchableSelect
              value={String(step.manager_level || 1)}
              onChange={(value) =>
                onChange({ ...step, manager_level: parseInt(value) })
              }
              options={MANAGER_LEVEL_OPTIONS}
              placeholder="Manager level"
              clearable={false}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              How many levels up the manager hierarchy
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
              Organizational Unit *
            </label>
            <SearchableSelect
              options={orgUnitOptions}
              value={step.org_unit_id || ""}
              onChange={(id) => onChange({ ...step, org_unit_id: id })}
              placeholder="Search and select an org unit..."
              isLoading={loadingOrgUnits}
              emptyMessage="No org units found"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Defines the manager context (users can have different managers in
              different units)
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
                Number of Approvals Needed *
              </label>
              <input
                type="number"
                min="1"
                value={step.required_approvals || 1}
                onChange={(e) =>
                  onChange({
                    ...step,
                    required_approvals: parseInt(e.target.value),
                  })
                }
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
              />
            </div>

            <div>
              <label className="flex items-center mt-8">
                <input
                  type="checkbox"
                  checked={step.parallel_approval || false}
                  onChange={(e) =>
                    onChange({ ...step, parallel_approval: e.target.checked })
                  }
                  className="w-4 h-4 rounded"
                />
                <span className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-50">
                  Parallel Approval
                </span>
              </label>
            </div>
          </div>
        </>
      )}

      {/* Org Unit Type */}
      {approversType === "org_unit" && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
              Organizational Unit *
            </label>
            <SearchableSelect
              options={orgUnitOptions}
              value={step.org_unit_id || ""}
              onChange={(id) => onChange({ ...step, org_unit_id: id })}
              placeholder="Search and select an org unit..."
              isLoading={loadingOrgUnits}
              emptyMessage="No org units found"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Any member of this org unit can approve
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
                Number of Approvals Needed *
              </label>
              <input
                type="number"
                min="1"
                value={step.required_approvals || 1}
                onChange={(e) =>
                  onChange({
                    ...step,
                    required_approvals: parseInt(e.target.value),
                  })
                }
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
              />
            </div>

            <div>
              <label className="flex items-center mt-8">
                <input
                  type="checkbox"
                  checked={step.parallel_approval || false}
                  onChange={(e) =>
                    onChange({ ...step, parallel_approval: e.target.checked })
                  }
                  className="w-4 h-4 rounded"
                />
                <span className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-50">
                  Parallel Approval
                </span>
              </label>
            </div>
          </div>
        </>
      )}

      {/* Escalation and Auto-Approval — Pro feature */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-50 mb-3">
          Escalation & Auto-Approval (Optional)
        </h4>

        {advancedEnabled ? (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
                Escalate After (minutes)
              </label>
              <input
                type="number"
                value={step.escalation_minutes || ""}
                onChange={(e) =>
                  onChange({
                    ...step,
                    escalation_minutes: e.target.value
                      ? parseInt(e.target.value)
                      : undefined,
                  })
                }
                placeholder="e.g., 1440"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Leave empty to disable escalation
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
                Auto-Approve After (minutes)
              </label>
              <input
                type="number"
                value={step.auto_approve_after_minutes || ""}
                onChange={(e) =>
                  onChange({
                    ...step,
                    auto_approve_after_minutes: e.target.value
                      ? parseInt(e.target.value)
                      : undefined,
                  })
                }
                placeholder="e.g., 2880"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Leave empty to disable auto-approval
              </p>
            </div>
          </div>
        ) : (
          <ProFeatureBadge>
            Escalation and auto-approval settings require Pro plan
          </ProFeatureBadge>
        )}
      </div>
    </div>
  );
};

// Vendor Selector Component with Search Integration
const VendorSelector = ({
  value,
  onChange,
  vendors,
  isLoading,
  onSearchChange,
  searchTerm,
}: {
  value: string;
  onChange: (id: string) => void;
  vendors: any[];
  isLoading: boolean;
  onSearchChange: (term: string) => void;
  searchTerm: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedVendor = vendors.find((v: any) => v.id === value);
  const displayValue = value ? selectedVendor?.name || "" : searchTerm;

  const handleSelectVendor = (vendorId: string) => {
    if (!isSelectableRef(vendorId)) {
      return;
    }
    onChange(vendorId);
    onSearchChange("");
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange("");
    onSearchChange("");
    setIsOpen(false);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">
        Specific Vendor (Optional)
      </label>

      <div className="relative">
        <input
          type="text"
          placeholder="Search vendors by name..."
          value={displayValue}
          onChange={(e) => {
            onSearchChange(e.target.value);
            if (!value) {
              setIsOpen(true);
            }
          }}
          onFocus={() => {
            setIsOpen(true);
          }}
          onBlur={() => {
            // Close dropdown after a delay to allow click handling
            setTimeout(() => setIsOpen(false), 200);
          }}
          className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
        />

        {/* Clear button when vendor is selected */}
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            ✕
          </button>
        )}

        {/* Dropdown menu */}
        {isOpen && !value && (
          <div className="absolute z-20 top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-lg">
            {isLoading ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                Loading vendors...
              </div>
            ) : vendors.length === 0 ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                {searchTerm
                  ? "No vendors found"
                  : "Start typing to search vendors..."}
              </div>
            ) : (
              <div>
                {vendors
                  .filter((v: any) => v && isSelectableRef(v.id))
                  .map((vendor: any, index: number) => {
                    const vendorId = vendor.id;
                    const vendorName =
                      vendor.legal_name || vendor.name || "Unknown Vendor";
                    const vendorNumber = getVendorReferenceText(vendor);

                    return (
                      <button
                        key={vendorId || index}
                        type="button"
                        onClick={() => handleSelectVendor(vendorId)}
                        className="w-full text-left px-4 py-3 hover:bg-blue-500 hover:text-white dark:hover:bg-blue-600 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0 flex flex-col"
                      >
                        <span className="font-medium">{vendorName}</span>
                        {vendorNumber && (
                          <span className="text-xs opacity-75">
                            {vendorNumber}
                          </span>
                        )}
                        {vendor.status && (
                          <span className="text-xs opacity-75 capitalize">
                            {vendor.status}
                          </span>
                        )}
                      </button>
                    );
                  })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Selected vendor display */}
      {value && selectedVendor && (
        <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            <span className="font-medium">Selected:</span>{" "}
            {selectedVendor.legal_name || selectedVendor.name}
            {getVendorReferenceText(selectedVendor) && (
              <span className="text-xs opacity-75">
                {" "}
                ({getVendorReferenceText(selectedVendor)})
              </span>
            )}
          </p>
        </div>
      )}

    </div>
  );
};

const ReviewStep = ({
  basicData,
  conditionsData,
  extractionFieldConditions,
}: any) => {
  const visibleExtractionFieldConditions = (
    extractionFieldConditions || []
  ).filter((condition: ExtractionFieldConditionDraft) => condition.field);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4">
          Review Your Approval Chain
        </h3>
      </div>

      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6 space-y-4">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Chain Name
          </p>
          <p className="text-gray-900 dark:text-gray-50 font-semibold">
            {basicData.name}
          </p>
        </div>

        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Approval Type
          </p>
          <p className="text-gray-900 dark:text-gray-50">
            {ACTION_OPTION_BY_VALUE.get(basicData.action_type)?.label ||
              basicData.action_type}
          </p>
        </div>

        <div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Document Type
            </p>
            <p className="text-gray-900 dark:text-gray-50 capitalize">
              {basicData.document_type}
            </p>
        </div>

        {(Object.entries(conditionsData).some(
          ([, v]) => v !== undefined && v !== "",
        ) ||
          visibleExtractionFieldConditions.length > 0) && (
          <div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
              Conditions
            </p>
            <div className="space-y-1 text-sm">
              {Object.entries(conditionsData).map(([key, value]) =>
                value !== undefined && value !== "" ? (
                  <p key={key} className="text-gray-700 dark:text-gray-300">
                    {key}: {String(value)}
                  </p>
                ) : null,
              )}
              {visibleExtractionFieldConditions.map(
                (condition: ExtractionFieldConditionDraft) => (
                  <p
                    key={condition.id}
                    className="text-gray-700 dark:text-gray-300"
                  >
                    {humanizeFieldKey(condition.field)}{" "}
                    {EXTRACTION_FIELD_OPERATORS.find(
                      (option) => option.value === condition.operator,
                    )?.label || condition.operator}
                    {operatorRequiresValue(condition.operator)
                      ? ` ${condition.value}`
                      : ""}
                  </p>
                ),
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
